import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const SKILL_INFO = {
  'Python':        { en: 'General-purpose language — great for automation, data & backends.', de: 'Vielseitige Sprache — ideal für Automatisierung, Daten & Backends.' },
  'JavaScript':    { en: 'The language of the web — runs in every browser.', de: 'Die Sprache des Webs — läuft in jedem Browser.' },
  'React':         { en: 'UI library by Meta — builds fast, interactive web apps.', de: 'UI-Bibliothek von Meta — für schnelle, interaktive Webapps.' },
  'FastAPI':       { en: 'Modern Python framework for building APIs at high speed.', de: 'Modernes Python-Framework für schnelle APIs.' },
  'Flask':         { en: 'Lightweight Python web framework — simple and flexible.', de: 'Schlankes Python-Webframework — einfach und flexibel.' },
  'Docker':        { en: 'Packages apps into containers so they run anywhere.', de: 'Verpackt Apps in Container damit sie überall laufen.' },
  'Git':           { en: 'Version control — tracks every change in your code.', de: 'Versionsverwaltung — verfolgt jede Änderung im Code.' },
  'SQLite':        { en: 'Lightweight database stored in a single file.', de: 'Leichtgewichtige Datenbank in einer einzigen Datei.' },
  'PostgreSQL':    { en: 'Powerful open-source relational database.', de: 'Leistungsstarke Open-Source-Datenbank.' },
  'MongoDB':       { en: 'NoSQL database that stores data as JSON-like documents.', de: 'NoSQL-Datenbank — speichert Daten als JSON-ähnliche Dokumente.' },
  'Selenium':      { en: 'Automates browsers — used for web scraping & testing.', de: 'Automatisiert Browser — für Web-Scraping & Tests.' },
  'PyQt6':         { en: 'Python framework for building desktop applications.', de: 'Python-Framework für Desktop-Anwendungen.' },
  'TypeScript':    { en: 'JavaScript with types — catches errors before they happen.', de: 'JavaScript mit Typen — fängt Fehler frühzeitig ab.' },
  'Discord.py':    { en: 'Python library to build Discord bots and automations.', de: 'Python-Bibliothek für Discord-Bots.' },
  'REST APIs':     { en: 'Standard for communication between web services.', de: 'Standard für Kommunikation zwischen Webdiensten.' },
  'Automation':    { en: 'Writing scripts that do repetitive tasks automatically.', de: 'Skripte die repetitive Aufgaben automatisch erledigen.' },
  'BeautifulSoup': { en: 'Python library for parsing and extracting HTML data.', de: 'Python-Bibliothek zum Auslesen von HTML-Daten.' },
  'Pandas':        { en: 'Python library for data analysis and manipulation.', de: 'Python-Bibliothek für Datenanalyse und -verarbeitung.' },
};

const SKILLS = Object.keys(SKILL_INFO);

function SkillItem({ skill, language }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);
  const spanRef = useRef(null);

  const handleEnter = () => {
    if (spanRef.current) {
      const r = spanRef.current.getBoundingClientRect();
      setPos({ x: r.left + r.width / 2, y: r.top });
    }
    timerRef.current = setTimeout(() => setVisible(true), 1500);
  };

  const handleLeave = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
  };

  const info = SKILL_INFO[skill];

  return (
    <>
      <span
        ref={spanRef}
        className="marquee-item"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {skill}<span className="marquee-sep">·</span>
      </span>

      {visible && info && createPortal(
        <motion.div
          className="skill-tooltip-fixed"
          style={{ left: pos.x, top: pos.y }}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          {info[language] || info.en}
        </motion.div>,
        document.body
      )}
    </>
  );
}

function MarqueeRow({ reverse = false, language }) {
  const doubled = [...SKILLS, ...SKILLS];
  const [paused, setPaused] = useState(false);

  return (
    <div
      className={`marquee-wrapper${reverse ? ' marquee-reverse' : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="marquee-track"
        style={{ animationPlayState: paused ? 'paused' : 'running' }}
      >
        {doubled.map((skill, i) => (
          <SkillItem key={i} skill={skill} language={language} />
        ))}
      </div>
    </div>
  );
}

export default function Skills() {
  const { t, language } = useLanguage();

  return (
    <section className="skills-v3">
      <motion.div
        className="skills-v3-header"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <span className="skills-v3-num">04</span>
        <div>
          <div className="section-tag" style={{ marginBottom: '8px' }}>Stack</div>
          <h2>{t('skills.title')}</h2>
        </div>
      </motion.div>

      <div className="marquee-outer">
        <MarqueeRow language={language} />
        <MarqueeRow reverse language={language} />
      </div>
    </section>
  );
}
